import { useMatch } from "react-router-dom-v5-compat";

import { Box, Icon, Inline, Text, Var } from "@seneca/senekit";

import {
  getAssignmentCourseId,
  getAssignmentExamQuestionMark,
  questionHasBeenStudied
} from "seneca-common/features/assignments/features/student-assignments/state";
import {
  isAssignmentExamQuestionFlagged,
  makeGetUnstudiedQuestionIds
} from "seneca-common/features/assignments/features/student-assignments/state/selectors";
import useEQEnrichedCourseSections from "seneca-common/features/exam-questions/hooks/useEQEnrichedCourseSections";
import {
  useMemoSelector,
  useSelector,
  useSelectorWithArgs
} from "seneca-common/utils/hooks/selectors";

import { RowLayoutLinkConditional } from "features/classroom/features/sidebar/features/common";
import { makeLinkToAssignmentExamQuestionsSessionWithState } from "features/classroom/routes/classroom/routes/course/routes/course-exam-questions/links";
import { getFocusedQuestionId } from "features/classroom/state";
import { ExamQuestionTitle } from "features/exam-questions/components/ExamQuestionTitle";
import { SELF_MARKING_DONT_KNOW_MARK } from "features/exam-questions/consts";
import { ROUTES } from "routing/routes";

type Props = {
  assignmentId: string;
  questionId: string;
  number: number;
};

export default function AssignmentQuestionRow({
  assignmentId,
  questionId,
  number
}: Props) {
  const courseId = useSelectorWithArgs(getAssignmentCourseId, assignmentId);

  const { data: courseExamQuestions } = useEQEnrichedCourseSections({
    courseId,
    assignmentId
  });

  const question = courseExamQuestions?.find(
    question => question.questionId === questionId
  );

  const isStudied = useSelectorWithArgs(
    questionHasBeenStudied,
    assignmentId,
    questionId
  );

  const mark = useSelectorWithArgs(
    getAssignmentExamQuestionMark,
    assignmentId,
    questionId
  );

  const isFlagged = useSelectorWithArgs(
    isAssignmentExamQuestionFlagged,
    assignmentId,
    questionId
  );

  const questionIdsToStudy = (useMemoSelector(
    makeGetUnstudiedQuestionIds,
    assignmentId
  )?.toJS() || []) as string[];

  const questionIdsToStudyWithoutCurrent = questionIdsToStudy.filter(
    id => id !== questionId
  );
  const link = makeLinkToAssignmentExamQuestionsSessionWithState({
    courseId,
    selectedQuestionIds: [questionId, ...questionIdsToStudyWithoutCurrent],
    assignmentId
  });

  const focusedQuestionId = useSelector(getFocusedQuestionId);
  const currentUrlMatch = useMatch(ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.path);

  const requiresTeacherReview = mark === SELF_MARKING_DONT_KNOW_MARK;
  return (
    <RowLayoutLinkConditional
      link={link}
      active={!!currentUrlMatch && questionId === focusedQuestionId}
      disabled={isStudied}
    >
      {{
        title: (
          <>
            {question && (
              <Inline space="large">
                <Text size="medium" truncate>
                  Q<Var>{number}</Var>
                </Text>
                <Box width="164px">
                  <ExamQuestionTitle question={question} />
                </Box>
              </Inline>
            )}
          </>
        ),
        indicator: isStudied ? (
          isFlagged || requiresTeacherReview ? (
            <Icon
              iconName="warning"
              accessibilityLabel="Flagged"
              size="medium"
            />
          ) : (
            <Text dataNoTranslate>
              <Inline flexWrap="nowrap" space="xsmall">
                {typeof mark === "number" ? mark : "-"} / {question?.marksTotal}
              </Inline>
            </Text>
          )
        ) : (
          <>-</>
        )
      }}
    </RowLayoutLinkConditional>
  );
}
